import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft20Filled } from '@fluentui/react-icons';
import { useAdminRates } from '../../../hooks/useAdminRates';
import { useB2bRates } from '../../../hooks/useB2bRates';
import { useManageUsers } from '../../../hooks/useManageUsers';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Download, Upload, Pencil, Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { useModal } from '../../../hooks/useModal';
import toast from 'react-hot-toast';

export default function UserRatesPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { open, close, Modal } = useModal();

  const activeTabType = (searchParams.get('type') || 'b2c').toLowerCase() === 'b2b' ? 'b2b' : 'b2c';

  const { useUserDetailQuery } = useManageUsers();
  const { data: userData } = useUserDetailQuery(userId || '');
  const user = userData;

  // ──────────────────────────────────────────────────────────────
  //  B2C RATES LOGIC & STATE
  // ──────────────────────────────────────────────────────────────
  const {
    useAllPlansQuery,
    usePlanRatesQuery,
    useChangeUserPlanMutation,
    useChangeUserB2bPlanMutation,
    useUploadRatesMutation,
    useUploadAndReplaceRatesMutation,
    downloadSampleRates,
    downloadUserRates,
    useSaveAsNewPlanMutation
  } = useAdminRates();

  const { data: b2cPlansData } = useAllPlansQuery();
  const rawB2cPlans = b2cPlansData?.data || [];
  const b2cPlans = useMemo(() => {
    return rawB2cPlans.filter((p: any) => {
      const type = (p.type || '').toLowerCase();
      const name = (p.name || '').toLowerCase();
      return type === 'b2c' || (!type && !name.startsWith('b2b_'));
    });
  }, [rawB2cPlans]);

  const currentB2cPlanId = user?.planId ? String(user.planId) : '';
  const [selectedB2cPlanId, setSelectedB2cPlanId] = useState<string>('');

  useEffect(() => {
    if (user?.planId) {
      setSelectedB2cPlanId(String(user.planId));
    }
  }, [user?.planId]);

  const { data: b2cRatesData, isLoading: isLoadingB2cRates } = usePlanRatesQuery(
    currentB2cPlanId,
    { enabled: !!currentB2cPlanId && activeTabType === 'b2c' }
  );
  const b2cRates = b2cRatesData?.data || [];

  const { mutate: changeB2cPlan, isPending: isChangingB2cPlan } = useChangeUserPlanMutation();
  const { mutate: saveAsNewB2cPlan, isPending: isSavingB2c } = useSaveAsNewPlanMutation();
  const { mutate: uploadRates, isPending: isUploadingB2c } = useUploadRatesMutation();
  const { mutate: uploadAndReplaceRates, isPending: isUploadingAndReplacingB2c } = useUploadAndReplaceRatesMutation();

  const b2cFileInputRef = useRef<HTMLInputElement>(null);
  const [b2cUploadMode, setB2cUploadMode] = useState<'create' | 'replace'>('create');
  const [isB2cDownloading, setIsB2cDownloading] = useState(false);

  const groupedB2cRates = useMemo(() => {
    const groups: Record<string, any> = {};
    b2cRates.forEach((rate: any) => {
      const key = `${rate.courierId}-${rate.serviceId}`;
      if (!groups[key]) {
        groups[key] = {
          courierId: rate.courierId,
          serviceId: rate.serviceId,
          courierName: rate.courierName,
          serviceName: rate.serviceName,
          mode: rate.mode,
          baseWeight: rate.baseWeightSlab,
          addlWeight: rate.addlWeightSlab,
          codCharges: rate.codCharges,
          codPercent: rate.codPercent,
          fuelSurcharge: rate.fuelSurcharge,
          fuelSurchargeAddl: rate.fuelSurchargeAddl,
          otherCharges: rate.otherCharges,
          rtoCharges: rate.rtoCharges,
          rtoChargesAddl: rate.rtoChargesAddl,
          zones: {}
        };
      }
      groups[key].zones[rate.zone] = { ...rate };
    });
    return Object.values(groups).sort((g1: any, g2: any) => {
      if (g1.mode !== g2.mode) {
        return g1.mode === "Air" ? -1 : 1;
      }
      const w1 = g1.baseWeight || 0.5;
      const w2 = g2.baseWeight || 0.5;
      if (w1 !== w2) {
        return w1 - w2;
      }
      if (g1.courierId !== g2.courierId) {
        return g1.courierId - g2.courierId;
      }
      return g1.serviceId - g2.serviceId;
    });
  }, [b2cRates]);

  const [editedB2cGroups, setEditedB2cGroups] = useState<any[]>([]);
  const [isB2cEditing, setIsB2cEditing] = useState(false);

  useEffect(() => {
    setEditedB2cGroups(JSON.parse(JSON.stringify(groupedB2cRates)));
  }, [groupedB2cRates]);

  const parseDecimal = (value: string | number) => {
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    return Math.round((parsed || 0) * 100) / 100;
  };

  const formatRate = (val: any) => {
    if (val === 0 || val === 0.0 || val === '0' || val === '0.00') return '0';
    if (!val && val !== 0) return '-';
    return Number(val).toFixed(1);
  };

  const handleB2cEditChange = (groupIndex: number, zone: string, field: string, value: string) => {
    if (value && !/^\d*\.?\d{0,2}$/.test(value)) return;
    const newGroups = [...editedB2cGroups];
    if (!newGroups[groupIndex].zones[zone]) {
      newGroups[groupIndex].zones[zone] = {
        courierId: newGroups[groupIndex].courierId,
        serviceId: newGroups[groupIndex].serviceId,
        zone: zone,
        baseWeightSlab: newGroups[groupIndex].baseWeight,
        addlWeightSlab: newGroups[groupIndex].addlWeight,
      };
    }
    newGroups[groupIndex].zones[zone][field] = value === '' ? 0 : parseDecimal(value);
    setEditedB2cGroups(newGroups);
  };

  const handleB2cGroupFieldChange = (groupIndex: number, field: string, value: string) => {
    if (value && !/^\d*\.?\d{0,2}$/.test(value)) return;
    const newGroups = [...editedB2cGroups];
    const numValue = value === '' ? 0 : parseDecimal(value);
    newGroups[groupIndex][field] = numValue;

    Object.keys(newGroups[groupIndex].zones).forEach(z => {
      newGroups[groupIndex].zones[z][field] = numValue;
    });

    setEditedB2cGroups(newGroups);
  };

  const handleApplyB2cPlan = () => {
    if (!selectedB2cPlanId || selectedB2cPlanId === String(currentB2cPlanId)) return;
    const planName = b2cPlans.find((p: any) => String(p.id) === selectedB2cPlanId)?.name;

    open({
      Component: () => (
        <div className="bg-white p-6 rounded-xl w-96 shadow-xl border border-gray-100">
          <h2 className="text-xl font-bold mb-3 text-text">Assign B2C Plan</h2>
          <p className="text-text2 mb-6">Are you sure you want to assign <strong>{planName}</strong> to this user?</p>
          <div className="flex gap-4">
            <Button variant="outline" className="flex-1" onClick={close}>Cancel</Button>
            <Button
              className="flex-1 bg-teal text-white"
              disabled={isChangingB2cPlan}
              onClick={() => {
                changeB2cPlan({ userId: userId!, planId: selectedB2cPlanId, planType: 'b2c' }, {
                  onSuccess: () => {
                    toast.success('B2C Plan assigned successfully');
                    close();
                  },
                  onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to change plan')
                });
              }}
            >
              {isChangingB2cPlan ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Confirm'}
            </Button>
          </div>
        </div>
      ),
      centered: true,
      closable: true,
      className: 'bg-transparent shadow-none',
    });
  };

  const handleSaveAsNewB2c = () => {
    open({
      Component: () => {
        const [planName, setPlanName] = useState('');
        return (
          <div className="bg-white p-6 rounded-xl w-96 shadow-xl border border-gray-100">
            <h2 className="text-xl font-bold mb-4 text-text">Save Custom B2C Plan</h2>
            <Input
              label="Plan Name"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="e.g. Custom Plan for User"
            />
            <div className="flex gap-4 mt-6">
              <Button variant="outline" className="flex-1" onClick={close}>Cancel</Button>
              <Button
                className="flex-1 bg-teal text-white"
                disabled={!planName.trim() || isSavingB2c || isChangingB2cPlan}
                onClick={() => {
                  if (b2cPlans.some((p: any) => p.name.toLowerCase() === planName.trim().toLowerCase())) {
                    toast.error('Plan name must be unique');
                    return;
                  }
                  const flatRates: any[] = [];
                  editedB2cGroups.forEach(g => {
                    Object.values(g.zones).forEach((z: any) => {
                      flatRates.push({
                        ...z,
                        codCharges: g.codCharges,
                        codPercent: g.codPercent,
                        fuelSurcharge: g.fuelSurcharge,
                        fuelSurchargeAddl: g.fuelSurchargeAddl,
                        otherCharges: g.otherCharges,
                      });
                    });
                  });
                  saveAsNewB2cPlan({ name: planName, rates: flatRates }, {
                    onSuccess: (res) => {
                      toast.success('Custom B2C plan saved');
                      const newPlanId = res.data;
                      if (newPlanId) {
                        changeB2cPlan({ userId: userId!, planId: String(newPlanId), planType: 'b2c' }, {
                          onSuccess: () => {
                            toast.success('Custom B2C plan assigned to user');
                            setIsB2cEditing(false);
                            close();
                          },
                          onError: (err: any) => {
                            toast.error(err.response?.data?.message || 'Failed to assign plan');
                            setIsB2cEditing(false);
                            close();
                          }
                        });
                      } else {
                        setIsB2cEditing(false);
                        close();
                      }
                    },
                    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to save plan')
                  });
                }}
              >
                {isSavingB2c || isChangingB2cPlan ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save & Assign'
                )}
              </Button>
            </div>
          </div>
        );
      },
      centered: true,
      closable: true,
      className: 'bg-transparent shadow-none',
    });
  };

  // ──────────────────────────────────────────────────────────────
  //  B2B RATES LOGIC & STATE
  // ──────────────────────────────────────────────────────────────
  const {
    useB2bPlansQuery,
    useB2bRatesQuery,
    useUpdateB2bRatesMutation,
    useAddChargeMutation,
    useUpdateChargeMutation,
    useDeleteChargeMutation,
    useUploadB2bRatesMutation,
    useUploadNewB2bPlanMutation,
    downloadB2bRates,
    downloadSampleB2bRates,
  } = useB2bRates();

  const { data: b2bPlansData, isLoading: isLoadingB2bPlans } = useB2bPlansQuery();
  const rawB2bPlans = (Array.isArray(b2bPlansData) ? b2bPlansData : (b2bPlansData as any)?.data) || [];
  const b2bPlans = useMemo(() => {
    return rawB2bPlans.filter((p: any) => {
      const type = (p.type || '').toLowerCase();
      const name = (p.name || '').toLowerCase();
      return type === 'b2b' || name.startsWith('b2b_');
    });
  }, [rawB2bPlans]);

  const currentB2bPlanId = user?.b2bPlanId ? String(user.b2bPlanId) : '';
  const [selectedB2bPlanId, setSelectedB2bPlanId] = useState<string>('');

  useEffect(() => {
    if (user?.b2bPlanId) {
      setSelectedB2bPlanId(String(user.b2bPlanId));
    }
  }, [user?.b2bPlanId]);

  const effectiveB2bPlanId = selectedB2bPlanId || currentB2bPlanId;

  const { data: b2bRatesData, isLoading: isLoadingB2bRates } = useB2bRatesQuery(
    effectiveB2bPlanId,
    { enabled: !!effectiveB2bPlanId && activeTabType === 'b2b' }
  );
  const couriers = b2bRatesData?.couriers || [];

  const [activeB2bCourierTab, setActiveB2bCourierTab] = useState<number | null>(null);
  const [isB2bEditing, setIsB2bEditing] = useState(false);
  const [isB2bDownloading, setIsB2bDownloading] = useState(false);
  const [isDownloadingB2bSample, setIsDownloadingB2bSample] = useState(false);

  const { mutate: updateB2bRates, isPending: isUpdatingB2bRates } = useUpdateB2bRatesMutation();
  const { mutate: addB2bCharge, isPending: isAddingB2bCharge } = useAddChargeMutation();
  const { mutate: updateB2bCharge, isPending: isUpdatingB2bCharge } = useUpdateChargeMutation();
  const { mutate: deleteB2bCharge } = useDeleteChargeMutation();
  const { mutate: uploadB2bRates, isPending: isUploadingB2bRates } = useUploadB2bRatesMutation();
  const { mutate: uploadNewB2bPlan, isPending: isUploadingNewB2b } = useUploadNewB2bPlanMutation();
  const { mutate: changeB2bPlan, isPending: isChangingB2bPlan } = useChangeUserB2bPlanMutation();

  const [editedB2bMatrix, setEditedB2bMatrix] = useState<any[]>([]);
  const [editedB2bCharges, setEditedB2bCharges] = useState<any[]>([]);
  const [showAddCharge, setShowAddCharge] = useState(false);
  const [newCharge, setNewCharge] = useState({ code: '', label: '', type: 'FIXED', value: '', min: '', unit: '' });

  const b2bFileInputRef = useRef<HTMLInputElement>(null);
  const newB2bPlanFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (couriers.length > 0 && activeB2bCourierTab === null) {
      setActiveB2bCourierTab(couriers[0].courierId);
    }
  }, [couriers, activeB2bCourierTab]);

  useEffect(() => {
    if (activeB2bCourierTab !== null) {
      const courier = couriers.find((c: any) => c.courierId === activeB2bCourierTab);
      if (courier) {
        setEditedB2bMatrix(JSON.parse(JSON.stringify(courier.matrix || [])));
        setEditedB2bCharges(JSON.parse(JSON.stringify(courier.charges || [])));
      }
    }
  }, [activeB2bCourierTab, couriers, isB2bEditing]);

  const activeCourier = couriers.find((c: any) => c.courierId === activeB2bCourierTab);

  const handleApplyB2bPlan = () => {
    if (!selectedB2bPlanId || selectedB2bPlanId === String(currentB2bPlanId)) return;
    const planName = b2bPlans.find((p: any) => String(p.id) === selectedB2bPlanId)?.name;

    open({
      Component: () => (
        <div className="bg-white p-6 rounded-xl w-96 shadow-xl border border-gray-100">
          <h2 className="text-xl font-bold mb-3 text-text">Assign B2B Plan</h2>
          <p className="text-text2 mb-6">Are you sure you want to assign B2B plan <strong>{planName}</strong> to this user?</p>
          <div className="flex gap-4">
            <Button variant="outline" className="flex-1" onClick={close}>Cancel</Button>
            <Button
              className="flex-1 bg-purple-700 hover:bg-purple-800 text-white"
              disabled={isChangingB2bPlan}
              onClick={() => {
                changeB2bPlan({ userId: userId!, b2bPlanId: selectedB2bPlanId }, {
                  onSuccess: () => {
                    toast.success('B2B Plan assigned successfully');
                    close();
                  },
                  onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to change B2B plan')
                });
              }}
            >
              {isChangingB2bPlan ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Confirm'}
            </Button>
          </div>
        </div>
      ),
      centered: true,
      closable: true,
      className: 'bg-transparent shadow-none',
    });
  };

  const handleB2bMatrixChange = (index: number, value: string) => {
    if (value && !/^\d*\.?\d{0,2}$/.test(value)) return;
    const newData = [...editedB2bMatrix];
    newData[index].ratePerKg = value === '' ? 0 : parseFloat(value);
    setEditedB2bMatrix(newData);
  };

  const handleB2bSave = () => {
    if (!effectiveB2bPlanId || !activeB2bCourierTab) return;
    updateB2bRates({
      planId: effectiveB2bPlanId,
      courierId: activeB2bCourierTab,
      payload: { matrix: editedB2bMatrix, charges: editedB2bCharges }
    }, {
      onSuccess: () => {
        toast.success('B2B rates updated successfully');
        setIsB2bEditing(false);
      },
      onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to update B2B rates')
    });
  };

  const handleAddChargeSubmit = () => {
    if (!effectiveB2bPlanId || !activeB2bCourierTab || !newCharge.label || !newCharge.value) {
      toast.error('Please fill required fields');
      return;
    }

    const newChargeObj = {
      chargeCode: newCharge.code || `CUST_${Date.now()}`,
      chargeLabel: newCharge.label,
      chargeType: newCharge.type,
      chargeValue: parseFloat(newCharge.value),
      minValue: newCharge.min ? parseFloat(newCharge.min) : null,
      unit: newCharge.unit || null,
    };
    const updatedCharges = [...editedB2bCharges, newChargeObj];

    updateB2bRates({
      planId: effectiveB2bPlanId,
      courierId: activeB2bCourierTab,
      payload: {
        matrix: editedB2bMatrix,
        charges: updatedCharges,
      }
    }, {
      onSuccess: () => {
        setEditedB2bCharges(updatedCharges);
        toast.success('Additional charge added');
        setShowAddCharge(false);
        setNewCharge({ code: '', label: '', type: 'FIXED', value: '', min: '', unit: '' });
      },
      onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to add charge')
    });
  };

  const handleB2bChargeChange = (index: number, field: string, value: string) => {
    if (field === 'chargeValue' || field === 'minValue') {
      if (value && !/^\d*\.?\d{0,2}$/.test(value)) return;
      const newData = [...editedB2bCharges];
      newData[index][field] = value === '' ? null : parseFloat(value);
      setEditedB2bCharges(newData);
    } else {
      const newData = [...editedB2bCharges];
      newData[index][field] = value;
      setEditedB2bCharges(newData);
    }
  };

  const handleEditB2bChargeClick = (charge: any, chargeIndex: number) => {
    open({
      Component: () => {
        const [label, setLabel] = useState(charge.chargeLabel || '');
        const [code, setCode] = useState(charge.chargeCode || '');
        const [type, setType] = useState(charge.chargeType || 'FIXED');
        const [value, setValue] = useState(charge.chargeValue !== null && charge.chargeValue !== undefined ? String(charge.chargeValue) : '');
        const [min, setMin] = useState(charge.minValue !== null && charge.minValue !== undefined ? String(charge.minValue) : '');
        const [unit, setUnit] = useState(charge.unit || '');

        return (
          <div className="bg-white p-6 rounded-xl w-96 max-w-full font-sans">
            <h2 className="text-xl font-bold mb-1 text-gray-900">Edit Additional Charge</h2>
            <p className="text-gray-500 text-sm mb-4">Update surcharge details for this courier.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Charge Label *</label>
                <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Fuel Surcharge" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Code</label>
                  <Input value={code} onChange={e => setCode(e.target.value)} placeholder="e.g. FSC" className="font-mono text-xs" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Type *</label>
                  <Select
                    value={type}
                    onChange={e => setType(e.target.value)}
                    options={[
                      { label: 'FIXED', value: 'FIXED' },
                      { label: 'PERCENTAGE', value: 'PERCENTAGE' },
                    ]}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Value *</label>
                  <Input type="number" value={value} onChange={e => setValue(e.target.value)} placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Min Value</label>
                  <Input type="number" value={min} onChange={e => setMin(e.target.value)} placeholder="0.00" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Unit</label>
                <Input value={unit} onChange={e => setUnit(e.target.value)} placeholder="e.g. per kg, per shipment" />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button
                className="bg-teal hover:bg-teal-700 text-white"
                disabled={!label.trim() || !value.trim() || isUpdatingB2bRates}
                onClick={() => {
                  if (!effectiveB2bPlanId || !activeB2bCourierTab) return;
                  const updatedCharges = editedB2bCharges.map((ch, idx) => {
                    if (idx === chargeIndex) {
                      return {
                        ...ch,
                        chargeCode: code || ch.chargeCode,
                        chargeLabel: label,
                        chargeType: type,
                        chargeValue: parseFloat(value),
                        minValue: min ? parseFloat(min) : null,
                        unit: unit || null,
                      };
                    }
                    return ch;
                  });

                  updateB2bRates({
                    planId: effectiveB2bPlanId,
                    courierId: activeB2bCourierTab,
                    payload: {
                      matrix: editedB2bMatrix,
                      charges: updatedCharges,
                    }
                  }, {
                    onSuccess: () => {
                      setEditedB2bCharges(updatedCharges);
                      toast.success('Charge updated successfully');
                      close();
                    },
                    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to update charge')
                  });
                }}
              >
                {isUpdatingB2bRates ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        );
      },
      centered: true,
      closable: true,
      className: 'bg-transparent shadow-none',
    });
  };

  const handleDeleteCharge = (chargeIndex: number) => {
    if (!effectiveB2bPlanId || !activeB2bCourierTab) return;
    if (window.confirm('Are you sure you want to remove this charge?')) {
      const updatedCharges = editedB2bCharges.filter((_, idx) => idx !== chargeIndex);
      updateB2bRates({
        planId: effectiveB2bPlanId,
        courierId: activeB2bCourierTab,
        payload: {
          matrix: editedB2bMatrix,
          charges: updatedCharges,
        }
      }, {
        onSuccess: () => {
          setEditedB2bCharges(updatedCharges);
          toast.success('Charge removed');
        },
        onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to remove charge')
      });
    }
  };

  const handleDownloadB2bRates = async () => {
    if (!effectiveB2bPlanId) return;
    try {
      setIsB2bDownloading(true);
      const blob = await downloadB2bRates(effectiveB2bPlanId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `b2b_rates_plan_${effectiveB2bPlanId}_user_${userId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download B2B rates');
    } finally {
      setIsB2bDownloading(false);
    }
  };

  const handleUploadB2bRates = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !effectiveB2bPlanId) return;
    if (!file.name.endsWith('.xlsx')) {
      toast.error('Please upload a valid Excel (.xlsx) file');
      return;
    }
    uploadB2bRates({ planId: effectiveB2bPlanId, file }, {
      onSuccess: () => {
        toast.success('B2B rates uploaded successfully');
        if (b2bFileInputRef.current) b2bFileInputRef.current.value = '';
      },
      onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to upload rates')
    });
  };

  const handleNewB2bPlanFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx')) {
      toast.error('Please upload a valid Excel (.xlsx) file');
      return;
    }

    open({
      Component: () => {
        const [planName, setPlanName] = useState('');
        return (
          <div className="bg-white p-6 rounded-xl w-96 font-sans shadow-xl border border-gray-100">
            <h2 className="text-xl font-bold mb-3 text-gray-800">New Custom B2B Plan</h2>
            <p className="text-gray-500 mb-4 text-sm">Enter a name for the custom B2B plan to create and assign to this user.</p>
            <Input
              placeholder="e.g. b2b_custom_user"
              value={planName}
              onChange={e => setPlanName(e.target.value)}
            />
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button
                className="bg-purple-700 hover:bg-purple-800 text-white"
                disabled={!planName.trim() || isUploadingNewB2b || isChangingB2bPlan}
                onClick={() => {
                  uploadNewB2bPlan({ planName, file }, {
                    onSuccess: (newPlan: any) => {
                      toast.success('B2B Plan created successfully');
                      if (newB2bPlanFileInputRef.current) newB2bPlanFileInputRef.current.value = '';
                      const newId = newPlan?.id || newPlan?.data?.id;
                      if (newId) {
                        changeB2bPlan({ userId: userId!, b2bPlanId: String(newId) }, {
                          onSuccess: () => {
                            toast.success('New B2B plan assigned to user');
                            close();
                          },
                          onError: () => close()
                        });
                      } else {
                        close();
                      }
                    },
                    onError: (err: any) => {
                      toast.error(err.response?.data?.message || 'Failed to upload new plan');
                      if (newB2bPlanFileInputRef.current) newB2bPlanFileInputRef.current.value = '';
                      close();
                    }
                  });
                }}
              >
                {isUploadingNewB2b || isChangingB2bPlan ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Upload & Assign
              </Button>
            </div>
          </div>
        );
      }
    });
  };

  const ZONES = ['zone-1', 'zone-2', 'zone-3', 'zone-4', 'zone-5'];
  const b2bOriginZones = Array.from(new Set(editedB2bMatrix.map(m => m.originZone))).sort();
  const b2bDestZones = Array.from(new Set(editedB2bMatrix.map(m => m.destinationZone))).sort();

  return (
    <div className="p-6 space-y-6 bg-gray-50/50 min-h-screen">
      {/* Top Header Card */}
      <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <button
          onClick={() => navigate('/admin/manage-users')}
          className="flex items-center gap-3 text-lg font-bold text-gray-800 hover:text-teal transition-colors"
        >
          <ArrowLeft20Filled /> Back to Manage Users
        </button>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="px-3.5 py-1.5 bg-teal text-white font-bold rounded-lg text-sm shadow-sm">
            CRN: {user?.crnNumber || 'Loading...'}
          </div>
          <div className={`px-3.5 py-1.5 border rounded-lg text-sm font-bold shadow-sm ${
            user?.active === 1 ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-600'
          }`}>
            {user?.active === 1 ? 'Active User' : 'Inactive'}
          </div>
          <div className="px-3.5 py-1.5 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 shadow-sm">
            Available Balance: ₹{user?.walletBalance ?? '0.00'}
          </div>
          <div className="px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs font-bold text-blue-700 shadow-sm">
            B2C Plan: {user?.planName || b2cPlans.find((p: any) => String(p.id) === currentB2cPlanId)?.name || (currentB2cPlanId ? `Plan #${currentB2cPlanId}` : 'Basic')}
          </div>
          <div className="px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-xs font-bold text-purple-700 shadow-sm">
            B2B Plan: {user?.b2bPlanName || b2bPlans.find((p: any) => String(p.id) === currentB2bPlanId)?.name || (currentB2bPlanId ? `Plan #${currentB2bPlanId}` : 'Not Assigned')}
          </div>
        </div>
      </div>

      {/* Segmented Mode Switcher */}
      <div className="flex items-center gap-3 border-b border-gray-200 pb-3">
        <button
          onClick={() => setSearchParams({ type: 'b2c' })}
          className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${
            activeTabType === 'b2c'
              ? 'bg-teal text-white shadow-md'
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          <span>🛍️ B2C Rate Card</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${activeTabType === 'b2c' ? 'bg-white/20 text-white' : 'bg-blue-50 text-blue-600'}`}>
            {b2cPlans.find((p: any) => String(p.id) === currentB2cPlanId)?.name || 'Basic'}
          </span>
        </button>

        <button
          onClick={() => setSearchParams({ type: 'b2b' })}
          className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${
            activeTabType === 'b2b'
              ? 'bg-purple-700 text-white shadow-md'
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          <span>🚚 B2B Rate Card</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${activeTabType === 'b2b' ? 'bg-white/20 text-white' : 'bg-purple-50 text-purple-700'}`}>
            {b2bPlans.find((p: any) => String(p.id) === currentB2bPlanId)?.name || (currentB2bPlanId ? `Plan #${currentB2bPlanId}` : 'None')}
          </span>
        </button>
      </div>

      {/* ──────────────────────────────────────────────────────────────
          TAB 1: B2C RATES CONTENT
          ────────────────────────────────────────────────────────────── */}
      {activeTabType === 'b2c' && (
        <div className="space-y-6">
          {/* Plan Selector & Actions Bar */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 flex flex-wrap items-end justify-between gap-4">
            <div className="flex items-end gap-3 flex-wrap">
              <div className="w-72">
                <label className="block text-sm font-semibold mb-2 text-gray-700">Assign B2C Plan</label>
                <Select
                  options={[
                    { label: 'Select B2C Plan...', value: '' },
                    ...b2cPlans.map((p: any) => ({ label: p.name, value: String(p.id) }))
                  ]}
                  value={selectedB2cPlanId}
                  onChange={(e) => setSelectedB2cPlanId(e.target.value)}
                />
              </div>

              <Button
                disabled={!selectedB2cPlanId || selectedB2cPlanId === String(currentB2cPlanId) || isChangingB2cPlan}
                onClick={handleApplyB2cPlan}
                className="bg-teal text-white h-[42px]"
              >
                {isChangingB2cPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply Plan'}
              </Button>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                className="flex items-center gap-2 h-[42px]"
                disabled={isB2cDownloading}
                onClick={async () => {
                  try {
                    if (!userId) return;
                    setIsB2cDownloading(true);
                    const blob = await downloadUserRates(userId);
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = currentB2cPlanId ? `rates_b2c_user_${userId}.xlsx` : 'b2c_rates_template.xlsx';
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    toast.error("Failed to download B2C rates");
                  } finally {
                    setIsB2cDownloading(false);
                  }
                }}
              >
                {isB2cDownloading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download size={16} /> Download {currentB2cPlanId ? 'B2C Rates' : 'Template'}
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                className="flex items-center gap-2 h-[42px]"
                onClick={async () => {
                  try {
                    const blob = await downloadSampleRates();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'b2c_rates_sample.xlsx';
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    toast.error("Failed to download B2C sample template");
                  }
                }}
              >
                <Download size={16} /> B2C Sample Sheet
              </Button>

              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                ref={b2cFileInputRef}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (b2cUploadMode === 'replace' && currentB2cPlanId) {
                    uploadAndReplaceRates({ planId: currentB2cPlanId, file }, {
                      onSuccess: () => {
                        toast.success('B2C rates replaced successfully');
                        if (b2cFileInputRef.current) b2cFileInputRef.current.value = '';
                      },
                      onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to replace rates')
                    });
                  } else {
                    const planName = `Custom_User_${userId}_${Date.now()}`;
                    uploadRates({ planName, file }, {
                      onSuccess: (res: any) => {
                        toast.success('B2C rates uploaded and new plan created');
                        if (b2cFileInputRef.current) b2cFileInputRef.current.value = '';
                        const newPlanId = res?.data;
                        if (newPlanId) {
                          changeB2cPlan({ userId: userId!, planId: String(newPlanId), planType: 'b2c' }, {
                            onSuccess: () => toast.success('New B2C plan assigned to user')
                          });
                        }
                      },
                      onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to upload rates')
                    });
                  }
                }}
              />

              {currentB2cPlanId && (
                <Button
                  variant="outline"
                  className="flex items-center gap-2 h-[42px]"
                  disabled={isUploadingAndReplacingB2c}
                  onClick={() => {
                    setB2cUploadMode('replace');
                    b2cFileInputRef.current?.click();
                  }}
                >
                  {isUploadingAndReplacingB2c ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload size={16} />}
                  Replace Current Rates
                </Button>
              )}

              <Button
                variant="outline"
                className="flex items-center gap-2 h-[42px] border-teal text-teal hover:bg-teal/5"
                disabled={isUploadingB2c}
                onClick={() => {
                  setB2cUploadMode('create');
                  b2cFileInputRef.current?.click();
                }}
              >
                {isUploadingB2c ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload size={16} />}
                Upload as New B2C Plan
              </Button>
            </div>
          </div>

          {/* B2C Rates Matrix Table Card */}
          {isLoadingB2cRates ? (
            <div className="bg-white p-12 rounded-xl text-center text-text3 shadow-sm border border-gray-200">
              <Loader2 className="w-8 h-8 animate-spin text-teal mx-auto mb-2" />
              Loading B2C rate cards...
            </div>
          ) : groupedB2cRates.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                <span className="font-bold text-gray-700">
                  B2C Rates Preview {isB2cEditing && <span className="text-teal font-normal">(Editing Mode)</span>}
                </span>
                <div className="flex gap-2">
                  {isB2cEditing ? (
                    <>
                      <Button variant="outline" onClick={() => setIsB2cEditing(false)}>Cancel</Button>
                      <Button className="bg-teal text-white flex items-center gap-2" onClick={handleSaveAsNewB2c}>
                        <Save size={16} /> Save Custom Plan & Assign
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" className="flex items-center gap-2" onClick={() => setIsB2cEditing(true)}>
                      <Pencil size={16} /> Edit Rates Individually
                    </Button>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left border-collapse">
                  <thead>
                    <tr className="bg-teal text-white font-bold border-b border-gray-300">
                      <th className="p-3 text-center border-r border-teal-600">Courier</th>
                      <th className="p-3 text-center border-r border-teal-600">Mode</th>
                      <th className="p-3 text-center border-r border-teal-600">Weight Slab</th>
                      {ZONES.map(z => (
                        <th key={z} colSpan={2} className="p-3 text-center border-r border-teal-600 uppercase tracking-wide">
                          {z}
                        </th>
                      ))}
                      <th className="p-3 text-center border-r border-teal-600">COD Min (₹)</th>
                      <th className="p-3 text-center border-r border-teal-600">COD %</th>
                      <th className="p-3 text-center border-r border-teal-600">Fuel Surcharge (%)</th>
                      <th className="p-3 text-center">Other Charges (₹)</th>
                    </tr>
                    <tr className="bg-teal text-white font-medium text-[11px] border-b border-gray-200">
                      <th className="border-r border-teal-600"></th>
                      <th className="border-r border-teal-600"></th>
                      <th className="border-r border-teal-600"></th>
                      {ZONES.map(z => (
                        <React.Fragment key={z}>
                          <th className="px-2 py-1 text-center border-r border-teal-600 font-semibold">FWD</th>
                          <th className="px-2 py-1 text-center border-r border-teal-600 font-semibold bg-red-800/40">RTO</th>
                        </React.Fragment>
                      ))}
                      <th className="border-r border-teal-600"></th>
                      <th className="border-r border-teal-600"></th>
                      <th className="border-r border-teal-600"></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {editedB2cGroups.map((group: any, idx: number) => (
                      <React.Fragment key={`${group.courierId}-${group.serviceId}`}>
                        <tr className="border-b border-gray-100 hover:bg-gray-50/50">
                          <td rowSpan={2} className="px-4 py-3 font-semibold text-text border-r border-gray-200 bg-gray-50/30 whitespace-nowrap">
                            {group.courierName}
                          </td>
                          <td rowSpan={2} className="px-2 py-3 text-center border-r border-gray-200 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              group.mode === 'Air' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {group.mode || 'Surface'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-text3 whitespace-nowrap text-center border-r border-gray-200">
                            Base {group.baseWeight}
                          </td>

                          {ZONES.map(z => (
                            <React.Fragment key={z}>
                              <td className="px-2 py-3 text-center border-r border-gray-200">
                                {isB2cEditing ? (
                                  <input
                                    type="number"
                                    className="w-16 border rounded px-1 py-1 text-center bg-gray-50"
                                    value={group.zones[z]?.baseRate || 0}
                                    onChange={(e) => handleB2cEditChange(idx, z, 'baseRate', e.target.value)}
                                  />
                                ) : (
                                  <span className="font-semibold text-text">{formatRate(group.zones[z]?.baseRate)}</span>
                                )}
                              </td>
                              <td className="px-2 py-3 text-center border-r border-gray-300 bg-red-50/20">
                                {isB2cEditing ? (
                                  <input
                                    type="number"
                                    className="w-16 border rounded px-1 py-1 text-center text-red-600"
                                    value={group.zones[z]?.rtoCharges || 0}
                                    onChange={(e) => handleB2cEditChange(idx, z, 'rtoCharges', e.target.value)}
                                  />
                                ) : (
                                  <span className="font-semibold text-red-600">{formatRate(group.zones[z]?.rtoCharges)}</span>
                                )}
                              </td>
                            </React.Fragment>
                          ))}

                          <td rowSpan={2} className="px-2 py-3 text-center border-r border-gray-200">
                            {isB2cEditing ? (
                              <input
                                type="number"
                                className="w-16 border rounded px-1 py-1 text-center"
                                value={group.codCharges || 0}
                                onChange={(e) => handleB2cGroupFieldChange(idx, 'codCharges', e.target.value)}
                              />
                            ) : (
                              formatRate(group.codCharges)
                            )}
                          </td>
                          <td rowSpan={2} className="px-2 py-3 text-center border-r border-gray-200">
                            {isB2cEditing ? (
                              <input
                                type="number"
                                className="w-16 border rounded px-1 py-1 text-center"
                                value={group.codPercent || 0}
                                onChange={(e) => handleB2cGroupFieldChange(idx, 'codPercent', e.target.value)}
                              />
                            ) : (
                              `${formatRate(group.codPercent)}%`
                            )}
                          </td>
                          <td rowSpan={2} className="px-2 py-3 text-center border-r border-gray-200">
                            {isB2cEditing ? (
                              <input
                                type="number"
                                className="w-16 border rounded px-1 py-1 text-center"
                                value={group.fuelSurcharge || 0}
                                onChange={(e) => handleB2cGroupFieldChange(idx, 'fuelSurcharge', e.target.value)}
                              />
                            ) : (
                              `${formatRate(group.fuelSurcharge)}%`
                            )}
                          </td>
                          <td rowSpan={2} className="px-2 py-3 text-center">
                            {isB2cEditing ? (
                              <input
                                type="number"
                                className="w-16 border rounded px-1 py-1 text-center"
                                value={group.otherCharges || 0}
                                onChange={(e) => handleB2cGroupFieldChange(idx, 'otherCharges', e.target.value)}
                              />
                            ) : (
                              formatRate(group.otherCharges)
                            )}
                          </td>
                        </tr>

                        <tr className="border-b-2 border-gray-200">
                          <td className="px-4 py-3 text-text3 whitespace-nowrap text-center border-r border-gray-200">
                            Add {group.addlWeight}
                          </td>
                          {ZONES.map(z => (
                            <React.Fragment key={z}>
                              <td className="px-2 py-3 text-center border-r border-gray-200">
                                {isB2cEditing ? (
                                  <input
                                    type="number"
                                    className="w-16 border rounded px-1 py-1 text-center bg-gray-50"
                                    value={group.zones[z]?.baseRateAddl || 0}
                                    onChange={(e) => handleB2cEditChange(idx, z, 'baseRateAddl', e.target.value)}
                                  />
                                ) : (
                                  <span className="text-text3">{formatRate(group.zones[z]?.baseRateAddl)}</span>
                                )}
                              </td>
                              <td className="px-2 py-3 text-center border-r border-gray-300 bg-red-50/20">
                                {isB2cEditing ? (
                                  <input
                                    type="number"
                                    className="w-16 border rounded px-1 py-1 text-center text-red-600"
                                    value={group.zones[z]?.rtoChargesAddl || 0}
                                    onChange={(e) => handleB2cEditChange(idx, z, 'rtoChargesAddl', e.target.value)}
                                  />
                                ) : (
                                  <span className="text-text3 text-red-600">{formatRate(group.zones[z]?.rtoChargesAddl)}</span>
                                )}
                              </td>
                            </React.Fragment>
                          ))}
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white p-12 rounded-xl text-center text-text3 shadow-sm border border-gray-200">
              {currentB2cPlanId ? 'No B2C rates configured for this plan.' : 'No B2C plan currently assigned to this user.'}
            </div>
          )}
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────
          TAB 2: B2B RATES CONTENT
          ────────────────────────────────────────────────────────────── */}
      {activeTabType === 'b2b' && (
        <div className="space-y-6">
          {/* Plan Selector & Actions Bar */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 flex flex-wrap items-end justify-between gap-4">
            <div className="flex items-end gap-3 flex-wrap">
              <div className="w-72">
                <label className="block text-sm font-semibold mb-2 text-gray-700">Assign B2B Plan</label>
                <Select
                  options={[
                    { label: 'Select B2B Plan...', value: '' },
                    ...b2bPlans.map((p: any) => ({ label: p.name, value: String(p.id) }))
                  ]}
                  value={selectedB2bPlanId}
                  onChange={(e) => setSelectedB2bPlanId(e.target.value)}
                />
              </div>

              <Button
                disabled={!selectedB2bPlanId || selectedB2bPlanId === String(currentB2bPlanId) || isChangingB2bPlan}
                onClick={handleApplyB2bPlan}
                className="bg-purple-700 hover:bg-purple-800 text-white h-[42px]"
              >
                {isChangingB2bPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply B2B Plan'}
              </Button>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                className="flex items-center gap-2 h-[42px]"
                disabled={!effectiveB2bPlanId || isB2bDownloading}
                onClick={handleDownloadB2bRates}
              >
                {isB2bDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download size={16} />}
                Rates Sheet
              </Button>

              <Button
                variant="outline"
                className="flex items-center gap-2 h-[42px]"
                disabled={isDownloadingB2bSample}
                onClick={async () => {
                  try {
                    setIsDownloadingB2bSample(true);
                    await downloadSampleB2bRates();
                  } catch (err) {
                    toast.error("Failed to download B2B sample template");
                  } finally {
                    setIsDownloadingB2bSample(false);
                  }
                }}
              >
                {isDownloadingB2bSample ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download size={16} />}
                B2B Sample Sheet
              </Button>

              <input
                type="file"
                accept=".xlsx"
                className="hidden"
                ref={b2bFileInputRef}
                onChange={handleUploadB2bRates}
              />

              {effectiveB2bPlanId && (
                <Button
                  variant="outline"
                  className="flex items-center gap-2 h-[42px]"
                  disabled={isUploadingB2bRates}
                  onClick={() => b2bFileInputRef.current?.click()}
                >
                  {isUploadingB2bRates ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload size={16} />}
                  Upload to Plan
                </Button>
              )}

              <input
                type="file"
                accept=".xlsx"
                className="hidden"
                ref={newB2bPlanFileInputRef}
                onChange={handleNewB2bPlanFileUpload}
              />

              <Button
                variant="outline"
                className="flex items-center gap-2 h-[42px] border-purple-600 text-purple-700 hover:bg-purple-50"
                disabled={isUploadingNewB2b}
                onClick={() => newB2bPlanFileInputRef.current?.click()}
              >
                {isUploadingNewB2b ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload size={16} />}
                Upload as New B2B Plan
              </Button>
            </div>
          </div>

          {/* B2B Courier Tabs & Rate Matrix */}
          {!effectiveB2bPlanId ? (
            <div className="bg-white p-12 text-center rounded-xl border border-gray-200 shadow-sm">
              <div className="w-16 h-16 mx-auto bg-purple-50 text-purple-700 rounded-full flex items-center justify-center mb-4">
                <Pencil className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">No B2B Plan Assigned</h3>
              <p className="text-gray-500 mt-2 mb-6 max-w-md mx-auto">
                This user does not have a B2B plan assigned yet. Select a B2B plan from the dropdown above to assign one, or upload a custom B2B rate sheet.
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  onClick={() => newB2bPlanFileInputRef.current?.click()}
                  className="bg-purple-700 text-white flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" /> Upload Custom B2B Plan
                </Button>
              </div>
            </div>
          ) : isLoadingB2bRates ? (
            <div className="bg-white p-12 text-center rounded-xl border border-gray-200 shadow-sm">
              <Loader2 className="w-8 h-8 animate-spin text-purple-700 mx-auto mb-2" />
              Loading B2B courier rates...
            </div>
          ) : couriers.length === 0 ? (
            <div className="bg-white p-12 text-center rounded-xl border border-gray-200 shadow-sm text-gray-500">
              No courier rates configured for this B2B plan.
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Courier Tabs */}
              <div className="flex border-b border-gray-200 bg-gray-50">
                {couriers.map((courier: any) => (
                  <button
                    key={courier.courierId}
                    onClick={() => { setActiveB2bCourierTab(courier.courierId); setIsB2bEditing(false); }}
                    className={`px-6 py-4 text-sm font-bold transition-colors ${
                      activeB2bCourierTab === courier.courierId
                        ? 'bg-white text-purple-700 border-b-2 border-purple-700 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {courier.courierName}
                  </button>
                ))}
              </div>

              {activeCourier && (
                <div className="p-6 space-y-8">
                  {/* Active Courier Header & Save/Edit */}
                  <div className="flex justify-between items-center pb-4 border-b border-gray-100">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">{activeCourier.courierName} NxN Rates Matrix</h3>
                      <p className="text-sm text-gray-500">Transport Mode: {activeCourier.mode || 'Surface'}</p>
                    </div>

                    <div className="flex items-center gap-3">
                      {isB2bEditing ? (
                        <>
                          <Button variant="outline" onClick={() => setIsB2bEditing(false)}>Cancel</Button>
                          <Button
                            className="bg-purple-700 hover:bg-purple-800 text-white flex items-center gap-2"
                            onClick={handleB2bSave}
                            disabled={isUpdatingB2bRates}
                          >
                            {isUpdatingB2bRates ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            Save Rates
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          className="border-purple-600 text-purple-700 hover:bg-purple-50 flex items-center gap-2"
                          onClick={() => setIsB2bEditing(true)}
                        >
                          <Pencil size={16} /> Edit Rates Individually
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* NxN Matrix Table */}
                  <div className="overflow-x-auto border border-gray-200 rounded-xl">
                    <table className="w-full text-xs text-left border-collapse">
                      <thead>
                        <tr className="bg-purple-800 text-white">
                          <th className="p-3 font-bold border-r border-purple-700 whitespace-nowrap">From / To</th>
                          {b2bDestZones.map(dz => (
                            <th key={dz} className="p-3 text-center font-bold border-r border-purple-700 uppercase whitespace-nowrap">
                              {dz} (₹/kg)
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {b2bOriginZones.map((oz) => (
                          <tr key={oz} className="border-b border-gray-100 hover:bg-purple-50/30">
                            <td className="p-3 font-bold text-gray-800 bg-gray-50 border-r border-gray-200 uppercase whitespace-nowrap">
                              {oz}
                            </td>
                            {b2bDestZones.map(dz => {
                              const cellIndex = editedB2bMatrix.findIndex(
                                m => m.originZone === oz && m.destinationZone === dz
                              );
                              const rate = cellIndex !== -1 ? editedB2bMatrix[cellIndex].ratePerKg : '-';
                              return (
                                <td key={dz} className="p-2 text-center border-r border-gray-200">
                                  {isB2bEditing && cellIndex !== -1 ? (
                                    <input
                                      type="number"
                                      step="0.01"
                                      className="w-16 border rounded px-1.5 py-1 text-center font-semibold text-purple-700 bg-white"
                                      value={rate === '-' ? '' : rate}
                                      onChange={(e) => handleB2bMatrixChange(cellIndex, e.target.value)}
                                    />
                                  ) : (
                                    <span className="font-semibold text-gray-800">
                                      {rate !== '-' ? `₹${Number(rate).toFixed(2)}` : '-'}
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Additional Charges Section */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-bold text-gray-800 text-base">Additional Surcharges & Fees</h4>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowAddCharge(!showAddCharge)}
                        className="flex items-center gap-1.5 text-purple-700 border-purple-200"
                      >
                        <Plus size={14} /> Add Surcharge
                      </Button>
                    </div>

                    {showAddCharge && (
                      <div className="p-4 bg-purple-50/50 border border-purple-100 rounded-xl grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
                        <Input
                          label="Code"
                          placeholder="e.g. FSC"
                          value={newCharge.code}
                          onChange={e => setNewCharge({ ...newCharge, code: e.target.value })}
                        />
                        <Input
                          label="Label"
                          placeholder="e.g. Fuel Surcharge"
                          value={newCharge.label}
                          onChange={e => setNewCharge({ ...newCharge, label: e.target.value })}
                        />
                        <Select
                          label="Type"
                          value={newCharge.type}
                          onChange={e => setNewCharge({ ...newCharge, type: e.target.value })}
                          options={[
                            { label: 'FIXED', value: 'FIXED' },
                            { label: 'PERCENTAGE', value: 'PERCENTAGE' },
                            { label: 'PER_KG', value: 'PER_KG' },
                          ]}
                        />
                        <Input
                          label="Value"
                          type="number"
                          placeholder="Rate"
                          value={newCharge.value}
                          onChange={e => setNewCharge({ ...newCharge, value: e.target.value })}
                        />
                        <Input
                          label="Min (₹)"
                          type="number"
                          placeholder="Optional"
                          value={newCharge.min}
                          onChange={e => setNewCharge({ ...newCharge, min: e.target.value })}
                        />
                        <div className="flex gap-2">
                          <Button
                            className="bg-purple-700 text-white w-full"
                            onClick={handleAddChargeSubmit}
                            disabled={isAddingB2bCharge}
                          >
                            Add
                          </Button>
                          <Button variant="outline" onClick={() => setShowAddCharge(false)}>Cancel</Button>
                        </div>
                      </div>
                    )}

                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-gray-100 text-gray-700 font-bold border-b border-gray-200">
                          <tr>
                            <th className="p-3">Charge Label</th>
                            <th className="p-3">Code</th>
                            <th className="p-3">Type</th>
                            <th className="p-3 text-right">Value</th>
                            <th className="p-3 text-right">Min Value</th>
                            <th className="p-3">Unit</th>
                            <th className="p-3 text-center w-24">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editedB2bCharges.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="p-6 text-center text-gray-400">
                                No additional charges configured for this courier.
                              </td>
                            </tr>
                          ) : (
                            editedB2bCharges.map((ch: any, idx: number) => (
                              <tr key={ch.id || ch.chargeCode || idx} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="p-3 font-semibold text-gray-800">
                                  {isB2bEditing ? (
                                    <input
                                      type="text"
                                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                                      value={ch.chargeLabel}
                                      onChange={(e) => handleB2bChargeChange(idx, 'chargeLabel', e.target.value)}
                                    />
                                  ) : (
                                    ch.chargeLabel
                                  )}
                                </td>
                                <td className="p-3 text-gray-500 font-mono text-[11px]">{ch.chargeCode}</td>
                                <td className="p-3">
                                  {isB2bEditing ? (
                                    <select
                                      className="border border-gray-300 rounded px-2 py-1 text-xs"
                                      value={ch.chargeType}
                                      onChange={(e) => handleB2bChargeChange(idx, 'chargeType', e.target.value)}
                                    >
                                      <option value="FIXED">FIXED</option>
                                      <option value="PERCENTAGE">PERCENTAGE</option>
                                    </select>
                                  ) : (
                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-700">
                                      {ch.chargeType}
                                    </span>
                                  )}
                                </td>
                                <td className="p-3 text-right font-bold text-gray-800">
                                  {isB2bEditing ? (
                                    <input
                                      type="number"
                                      className="w-20 border border-gray-300 rounded px-2 py-1 text-xs text-right"
                                      value={ch.chargeValue ?? ''}
                                      onChange={(e) => handleB2bChargeChange(idx, 'chargeValue', e.target.value)}
                                    />
                                  ) : (
                                    ch.chargeType === 'PERCENTAGE' ? `${ch.chargeValue}%` : `₹${ch.chargeValue}`
                                  )}
                                </td>
                                <td className="p-3 text-right text-gray-600">
                                  {isB2bEditing ? (
                                    <input
                                      type="number"
                                      className="w-20 border border-gray-300 rounded px-2 py-1 text-xs text-right"
                                      value={ch.minValue ?? ''}
                                      onChange={(e) => handleB2bChargeChange(idx, 'minValue', e.target.value)}
                                    />
                                  ) : (
                                    ch.minValue ? `₹${ch.minValue}` : '-'
                                  )}
                                </td>
                                <td className="p-3 text-gray-500">
                                  {isB2bEditing ? (
                                    <input
                                      type="text"
                                      className="w-24 border border-gray-300 rounded px-2 py-1 text-xs"
                                      value={ch.unit || ''}
                                      onChange={(e) => handleB2bChargeChange(idx, 'unit', e.target.value)}
                                    />
                                  ) : (
                                    ch.unit || '-'
                                  )}
                                </td>
                                <td className="p-3 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    {!isB2bEditing && (
                                      <button
                                        type="button"
                                        onClick={() => handleEditB2bChargeClick(ch, idx)}
                                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1 rounded"
                                        title="Edit charge"
                                      >
                                        <Pencil size={14} />
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (isB2bEditing) {
                                          setEditedB2bCharges(editedB2bCharges.filter((_, i) => i !== idx));
                                        } else {
                                          handleDeleteCharge(idx);
                                        }
                                      }}
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded"
                                      title="Delete charge"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Modal />
    </div>
  );
}
